import { createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../utils/constants/api";
import { ApiResponse } from "../../../types/APIResponse";
import { SchedulerIntervalData } from "./schedulerSettingsSlice";
import { apiEndpoints } from "../../../utils/constants/apiEndpoints";

export const fetchSchedulerInterval = createAsyncThunk(
  "schedulerSettings/fetchInterval",
  async (_, { rejectWithValue }) => {
    try {
      const response: ApiResponse<SchedulerIntervalData> = await api.get(apiEndpoints.schedulerSettings.getLostSchedulerInterval);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch scheduler settings");
    }
  }
);

interface SchedulerUpdatePayload {
  openToPendingMinutes?: number;
  pendingToReviewMinutes?: number;
  reviewToInProcessMinutes?: number;
  unpaidCleanupEnabled?: boolean;
  incompleteDraftDays?: number;
  unpaidClaimDays?: number;
  recentPaymentHighlightHours?: number;
}

export const updateSchedulerInterval = createAsyncThunk(
  "schedulerSettings/updateInterval",
  async (updates: SchedulerUpdatePayload, { rejectWithValue }) => {
    try {
      const payload: SchedulerUpdatePayload = {};
      if (updates.openToPendingMinutes !== undefined) payload.openToPendingMinutes = Number(updates.openToPendingMinutes);
      if (updates.pendingToReviewMinutes !== undefined) payload.pendingToReviewMinutes = Number(updates.pendingToReviewMinutes);
      if (updates.reviewToInProcessMinutes !== undefined) payload.reviewToInProcessMinutes = Number(updates.reviewToInProcessMinutes);
      if (updates.unpaidCleanupEnabled !== undefined) payload.unpaidCleanupEnabled = Boolean(updates.unpaidCleanupEnabled);
      if (updates.incompleteDraftDays !== undefined) payload.incompleteDraftDays = Number(updates.incompleteDraftDays);
      if (updates.unpaidClaimDays !== undefined) payload.unpaidClaimDays = Number(updates.unpaidClaimDays);
      if (updates.recentPaymentHighlightHours !== undefined) payload.recentPaymentHighlightHours = Number(updates.recentPaymentHighlightHours);

      await api.post(apiEndpoints.schedulerSettings.updateLostSchedulerInterval, { data: payload });
      return payload;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to update scheduler settings");
    }
  }
);

export const runUnpaidCleanupNow = createAsyncThunk(
  "schedulerSettings/runUnpaidCleanupNow",
  async (_, { rejectWithValue }) => {
    try {
      const response: ApiResponse<{ totalDeleted: number; deletedDrafts: number; deletedUnpaidClaims: number }> = await api.post(
        apiEndpoints.schedulerSettings.runUnpaidCleanupNow,
        { data: {} }
      );
      return {
        message: response.message || `Cleanup complete. ${response.data?.totalDeleted ?? 0} claim(s) soft-deleted.`,
        totalDeleted: response.data?.totalDeleted ?? 0,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Manual cleanup failed");
    }
  }
);

// Manual trigger for Review → InProcess promotion. Same handler the hourly cron runs;
// used after deploy / config change so admins don't have to wait for the next top-of-hour
// tick to see eligible claims move into the In Process column.
export const runReviewToInProcessNow = createAsyncThunk(
  "schedulerSettings/runReviewToInProcessNow",
  async (_, { rejectWithValue }) => {
    try {
      const response: ApiResponse<{ moved: number; cutoff: string | null; reason?: string }> = await api.post(
        apiEndpoints.schedulerSettings.runReviewToInProcessNow,
        { data: {} }
      );
      return {
        message: response.message || `Promoted ${response.data?.moved ?? 0} claim(s).`,
        moved: response.data?.moved ?? 0,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Manual promotion failed");
    }
  }
);
